/**
 * Runtime CSS for the inline `pb-font-<slug>` class system.
 *
 * Mirrors [image-runtime-css.js](builder/scripts/editing/image-runtime-css.js):
 *   - one `.pb-font-<slug> { font-family: '<name>', <fallback>; }` rule per
 *     family actually used by the current project
 *   - idempotent `withFontRuntimeCSS()` appender keyed off a marker comment
 *
 * Span + class are already allowed by the sanitizer; classes survive
 * sanitize() unchanged, so wrapping a selection in
 * `<span class="pb-font-inter">…</span>` is safe.
 *
 * The rules emitted here are inert until the matching font is actually
 * loaded — see the iframe-renderer / export / bridge live-inject paths
 * which call `buildLoaderTagsForFamily()` to add the matching <link>.
 */

import { genericFallbackFamily, slugifyFontName } from '../shared/font-catalog.js';

const FONT_RUNTIME_MARKER = 'pb-font-runtime-v1';

export function buildFontRuntimeRule(familyName) {
  const slug = slugifyFontName(familyName);
  if (!slug) return '';
  const fallback = genericFallbackFamily(familyName);
  return '.pb-font-' + slug + "{font-family:'" + familyName + "', " + fallback + '}';
}

export function buildFontRuntimeCSS(families = []) {
  const rules = ['/* ' + FONT_RUNTIME_MARKER + ' */'];
  const seen = new Set();
  for (const name of Array.isArray(families) ? families : []) {
    if (!name || seen.has(name)) continue;
    seen.add(name);
    const rule = buildFontRuntimeRule(name);
    if (rule) rules.push(rule);
  }
  return rules.join('\n');
}

// Kept for compatibility with callers/tests that import the constant. The
// catalog-wide rule block was intentionally removed; document/export builders
// append usage-specific rules via buildFontRuntimeCSS().
export const FONT_RUNTIME_CSS = buildFontRuntimeCSS();

export function withFontRuntimeCSS(css = '', families = []) {
  const rawCSS = typeof css === 'string' ? css : '';
  if (rawCSS.includes(FONT_RUNTIME_MARKER)) return rawCSS;
  const baseCSS = rawCSS.trimEnd();
  return [baseCSS, buildFontRuntimeCSS(families)].filter(Boolean).join('\n\n');
}
